/**
 * Centralized, validated access to the frontend environment.
 *
 * Every `import.meta.env` read in the app goes through this module — application
 * code imports `env` and never touches `import.meta.env` directly. That keeps the
 * full list of variables the app depends on in one place, gives them real types,
 * and turns a missing value into one clear error at startup instead of an
 * `undefined` that surfaces much later as a broken request.
 *
 * Adding a variable:
 *   1. add it to `.env` and `.env.example`
 *   2. declare it in `src/vite-env.d.ts`
 *   3. read it below with `required()` or `optional()`
 *
 * Note: only names prefixed with `VITE_` are exposed to the browser by Vite, and
 * everything here ships in the client bundle — never put a secret in a VITE_ var.
 */

/** Collected during parsing so we can report *every* missing var at once. */
const missing: string[] = [];

function read(key: string): string | undefined {
  const value = (import.meta.env as Record<string, unknown>)[key];
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

/** A variable the app cannot start without. */
function required(key: string): string {
  const value = read(key);
  if (value === undefined) {
    missing.push(key);
    return '';
  }
  return value;
}

/** A variable with a sane default when unset. */
function optional(key: string, fallback: string): string {
  return read(key) ?? fallback;
}

/** `http://host:4000/` and `http://host:4000` must behave identically. */
function stripTrailingSlash(url: string): string {
  return url.replace(/\/+$/, '');
}

/**
 * Base URL the API client prefixes onto every request path.
 *
 * Left unset it resolves to '' (same-origin), so requests go to `/api/v1/...` on
 * the page's own origin — which is what the Vite dev proxy and a single-domain
 * production deploy both expect. Setting it points the app at an API on another
 * origin instead.
 */
const apiBaseUrl = stripTrailingSlash(optional('VITE_API_BASE_URL', ''));

const supabaseUrl = required('VITE_SUPABASE_URL');
const supabaseAnonKey = required('VITE_SUPABASE_ANON_KEY');

const appName = optional('VITE_APP_NAME', 'AI Question Generator');

if (missing.length > 0) {
  throw new Error(
    `Missing required environment variable(s): ${missing.join(', ')}.\n` +
      `Add them to frontend/.env (see frontend/.env.example), then restart the dev server — ` +
      `Vite only reads .env at startup.`
  );
}

export const env = Object.freeze({
  apiBaseUrl,
  supabaseUrl,
  supabaseAnonKey,
  appName,
  /** Vite's mode: 'development' | 'production' | a custom --mode. */
  mode: import.meta.env.MODE,
  isDev: import.meta.env.DEV,
  isProd: import.meta.env.PROD,
});

export type Env = typeof env;
